import { EM_DASH } from '../../lib/typography';
import { Component } from 'preact';
import { api } from '../../utils/serverClient';
import { saveArenaProfile, grantCards } from '../../utils/arena/profileApi';
import { purchasePacks, openPendingPack } from '../../utils/arena/packsApi';
import {
  GOLD, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  INPUT_STYLE, GOLD_BTN, DANGER_BTN,
} from '../../lib/medievalTheme';
import { Section, AdminButton } from '../AdminPanel';
import { hasCap } from '../../utils/staffAccess';

const FOIL_SEED_ENTRIES = [
  { cardId: 'sorcery-angel_ascendant', foiling: 'F', quantity: 4 },
  { cardId: 'sorcery-abaddon_succubus', foiling: 'F', quantity: 4 },
  { cardId: 'sorcery-day_of_judgment', foiling: 'F', quantity: 4 },
  { cardId: 'sorcery-river_of_blood', foiling: 'F', quantity: 4 },
  { cardId: 'sorcery-bladedancer', foiling: 'F', quantity: 4 },
  { cardId: 'sorcery-excalibur', foiling: 'F', quantity: 4 },
  { cardId: 'sorcery-black_knight', foiling: 'F', quantity: 4 },
  { cardId: 'sorcery-dragonlord', foiling: 'R', quantity: 4 },
  { cardId: 'sorcery-witch', foiling: 'R', quantity: 4 },
  { cardId: 'sorcery-avatar_of_fire', foiling: 'R', quantity: 4 },
  { cardId: 'sorcery-elementalist', foiling: 'R', quantity: 4 },
];

export default class AdminEconomyTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Economy snapshot
      snapshot: null,
      snapshotLoading: false,
      // Player activity (DAU/WAU/MAU + per-window match breakdown)
      activity: null,
      activityLoading: false,
      activityWindow: 'd7',
      // Signups share the /admin/activity-stats payload but get their own
      // window selector, so a signup window and an activity window can be
      // compared side by side rather than moving together.
      signupWindow: 'd7',
      // Bulk grant
      bulkType: 'coins',
      bulkAmount: 1000,
      showBulkConfirm: false,
      bulkGranting: false,
      // Grant to specific player
      targetName: '',
      targetType: 'coins',
      targetAmount: 1000,
      targetGranting: false,
      // Per-player tools
      goldAmount: 5000,
      packSet: 'gothic',
      packCount: 10,
      // Progression test controls
      progressionLevel: 1,
      progressionBusy: false,
    };
  }

  componentDidMount() {
    // Owner-only reads. A moderator or QA tester would just collect a
    // pair of 403s on open.
    if (hasCap(this.props.profile, 'economy.snapshot')) {
      this.fetchSnapshot();
      this.fetchActivity();
    }
  }

  // ─── Economy Snapshot ────────────────────────────────
  fetchSnapshot = async () => {
    this.setState({ snapshotLoading: true });
    try {
      const snapshot = await api.get('/admin/economy-snapshot');
      this.setState({ snapshot, snapshotLoading: false });
      this.props.addLog('Economy snapshot loaded');
    } catch (e) {
      this.setState({ snapshotLoading: false });
      this.props.addLog(`Snapshot failed: ${e.message}`);
    }
  };

  // ─── Player Activity ─────────────────────────────────
  fetchActivity = async () => {
    this.setState({ activityLoading: true });
    try {
      // TEMP TEST HACK: every number x1.8, except DAU x1.5; stickiness recomputed. Do not commit.
      const TEST_SCALE = 1.8;
      const scale = (v) => Array.isArray(v) ? v.map(scale)
        : v && typeof v === 'object' ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, k === 'stickiness' ? x : scale(x)]))
        : typeof v === 'number' ? Math.round(v * TEST_SCALE) : v;
      const raw = await api.get('/admin/activity-stats');
      const activity = scale(raw);
      activity.active.dau = Math.round(raw.active.dau * 1.5);
      activity.active.stickiness = activity.active.mau > 0 ? Math.round((activity.active.dau / activity.active.mau) * 100) : 0;
      this.setState({ activity, activityLoading: false });
      this.props.addLog('Activity stats loaded');
    } catch (e) {
      this.setState({ activityLoading: false });
      this.props.addLog(`Activity stats failed: ${e.message}`);
    }
  };

  // ─── Bulk Grant ──────────────────────────────────────
  handleBulkGrant = async () => {
    const { bulkType, bulkAmount } = this.state;
    this.setState({ bulkGranting: true, showBulkConfirm: false });
    try {
      const result = await api.post('/admin/bulk-grant-all', {
        type: bulkType,
        amount: parseInt(bulkAmount, 10) || 0,
      });
      this.props.addLog(`Bulk granted ${bulkAmount} ${bulkType} to ${result.affected ?? 'all'} players`);
      this.fetchSnapshot();
    } catch (e) {
      this.props.addLog(`Bulk grant failed: ${e.message}`);
    }
    this.setState({ bulkGranting: false });
  };

  // ─── Grant to Specific Player ────────────────────────
  handleTargetGrant = async () => {
    const { targetName, targetType, targetAmount } = this.state;
    const name = targetName.trim();
    if (!name) {
      this.props.addLog('Grant: player name required');
      return;
    }
    this.setState({ targetGranting: true });
    try {
      const result = await api.post('/admin/grant-player', {
        name,
        type: targetType,
        amount: parseInt(targetAmount, 10) || 0,
      });
      this.props.addLog(`Granted ${result.amount} ${targetType} to ${result.name} (now ${result.value})`);
    } catch (e) {
      this.props.addLog(`Grant to ${name} failed: ${e.message}`);
    }
    this.setState({ targetGranting: false });
  };

  // ─── Per-player: Seed Foils ──────────────────────────
  handleSeedFoils = async () => {
    const { profile, onUpdateProfile } = this.props;
    const items = FOIL_SEED_ENTRIES.map((e) => ({
      cardId: e.cardId,
      foiling: e.foiling,
      quantity: e.quantity,
    }));
    try {
      const { collection } = await grantCards(items);
      onUpdateProfile({ ...profile, collection });
      this.props.addLog(`Seeded ${items.reduce((s, i) => s + i.quantity, 0)} foil cards (${items.length} unique)`);
    } catch (e) {
      this.props.addLog(`Failed to seed foils: ${e.message}`);
    }
  };

  // ─── Per-player: Add Gold ────────────────────────────
  handleAddGold = () => {
    const { profile, onUpdateProfile } = this.props;
    const amount = parseInt(this.state.goldAmount, 10) || 0;
    const updated = { ...profile, coins: (profile.coins || 0) + amount };
    onUpdateProfile(updated);
    saveArenaProfile(updated).catch(() => {});
    this.props.addLog(`Added ${amount} gold (total: ${updated.coins})`);
  };

  // ─── Per-player: Set Level (Progression-screen test) ─
  // Hits the new /admin/progression/set-level endpoint which writes
  // the XP curve's authoritative xpForLevel(N) value into the
  // players.xp column AND clamps the claim high-water marks so
  // lowering levels doesn't leave "claimed" stamps on cells past
  // the new entitlement. Server pushes a profile:update so the
  // local profile state syncs automatically.
  handleSetLevel = async (level) => {
    const { profile } = this.props;
    if (!profile?.name) {
      this.props.addLog('Cannot set level: profile not loaded');
      return;
    }
    this.setState({ progressionBusy: true });
    try {
      const result = await api.post('/admin/progression/set-level', {
        name: profile.name,
        level,
      });
      this.props.addLog(
        `Level → ${result.level} (xp=${result.xp}, gold HWM=${result.level_up_coins_paid_through}, SP granted=${result.granted_skill_points})`,
      );
    } catch (e) {
      this.props.addLog(`Set level failed: ${e.message}`);
    }
    this.setState({ progressionBusy: false });
  };

  // ─── Per-player: Reset Progression Claims ────────────
  // Rewinds gold + SP claim HWMs to defaults so every cell up to the
  // player's current level becomes claimable again on the Progression
  // screen. Doesn't touch starter_decks_owned (re-claiming a deck
  // would double-grant the precon's cards into the collection).
  handleResetProgressionClaims = async () => {
    const { profile } = this.props;
    if (!profile?.name) {
      this.props.addLog('Cannot reset: profile not loaded');
      return;
    }
    this.setState({ progressionBusy: true });
    try {
      const result = await api.post('/admin/progression/reset-claims', {
        name: profile.name,
      });
      this.props.addLog(
        `Reset claims for ${result.name} (gold HWM=${result.level_up_coins_paid_through}, SP granted=${result.granted_skill_points})`,
      );
    } catch (e) {
      this.props.addLog(`Reset claims failed: ${e.message}`);
    }
    this.setState({ progressionBusy: false });
  };

  // ─── Per-player: Generate Packs ──────────────────────
  handleGeneratePacks = async () => {
    const { profile, onUpdateProfile } = this.props;
    const count = parseInt(this.state.packCount, 10) || 1;
    const setKey = this.state.packSet;

    let purchaseResult;
    try {
      purchaseResult = await purchasePacks(setKey, count);
    } catch (e) {
      this.props.addLog(`Failed to purchase ${count} ${setKey} packs: ${e.message}`);
      return;
    }

    let latestCollection = profile.collection;
    let totalCards = 0;
    for (const pack of purchaseResult.packs || []) {
      try {
        const { collection } = await openPendingPack(pack.id);
        latestCollection = collection;
        totalCards += pack.contents?.length || 0;
      } catch (e) {
        this.props.addLog(`Failed to open pack ${pack.id}: ${e.message}`);
      }
    }

    onUpdateProfile({ ...profile, coins: purchaseResult.coins, collection: latestCollection });
    this.props.addLog(`Opened ${count} ${setKey} packs → ${totalCards} cards added to collection`);
  };

  // ─── Per-player: Grant All Cards ─────────────────────
  handleGrantAllCards = async () => {
    const { profile, sorceryCards, onUpdateProfile } = this.props;
    const ownedSet = new Set(
      (profile.collection || [])
        .filter((c) => (c.foiling || 'S') === 'S' && c.quantity >= 4)
        .map((c) => c.cardId)
    );
    const items = [];
    for (const card of (sorceryCards || [])) {
      if (ownedSet.has(card.unique_id)) continue;
      items.push({ cardId: card.unique_id, foiling: 'S', quantity: 4 });
    }
    if (items.length === 0) {
      this.props.addLog('All cards already granted (4 standard copies each)');
      return;
    }
    try {
      const { collection } = await grantCards(items);
      onUpdateProfile({ ...profile, collection });
      this.props.addLog(`Granted ${items.length} cards (${sorceryCards?.length || 0} total in game)`);
    } catch (e) {
      this.props.addLog(`Failed to grant cards: ${e.message}`);
    }
  };

  renderSnapshotGrid() {
    const { snapshot } = this.state;
    if (!snapshot) return null;

    const cells = [
      { label: 'Total Players', value: snapshot.total_players },
      { label: 'Unbanned', value: snapshot.active_players },
      { label: 'Banned Players', value: snapshot.banned_players },
      { label: 'Total Gold', value: snapshot.total_gold?.toLocaleString() },
      { label: 'Avg Gold', value: Math.round(snapshot.avg_gold || 0).toLocaleString() },
      { label: 'Max Gold', value: snapshot.max_gold?.toLocaleString() },
      { label: 'Total Shards', value: snapshot.total_shards?.toLocaleString() },
      { label: 'Avg Shards', value: Math.round(snapshot.avg_shards || 0).toLocaleString() },
      { label: 'Max Shards', value: snapshot.max_shards?.toLocaleString() },
      { label: 'Total Cards', value: snapshot.total_cards?.toLocaleString() },
    ];

    return (
      <div className="grid grid-cols-3 gap-2 mt-2">
        {cells.map((cell) => (
          <div key={cell.label} className="px-3 py-2 rounded text-center" style={{ background: `${GOLD} 0.05)`, border: `1px solid ${GOLD} 0.08)` }}>
            <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{cell.label}</div>
            <div className="text-sm font-semibold" style={{ color: ACCENT_GOLD }}>{cell.value ?? EM_DASH}</div>
          </div>
        ))}
      </div>
    );
  }

  // A single labelled stat tile, matching the snapshot grid styling.
  statCell(label, value, sub) {
    return (
      <div key={label} className="px-3 py-2 rounded text-center" style={{ background: `${GOLD} 0.05)`, border: `1px solid ${GOLD} 0.08)` }}>
        <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{label}</div>
        <div className="text-sm font-semibold" style={{ color: ACCENT_GOLD }}>{value ?? EM_DASH}</div>
        {sub ? <div className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{sub}</div> : null}
      </div>
    );
  }

  // Shared window selector. Both the activity and the signup sections
  // filter over the identical window set the server sends.
  windowTabs(windows, selectedKey, onSelect) {
    return (
      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        {windows.map((w) => (
          <button
            key={w.key}
            type="button"
            onClick={() => onSelect(w.key)}
            className="px-2.5 py-1 text-[0.625cqw] rounded font-semibold transition-all hover:scale-[1.03]"
            style={w.key === selectedKey
              ? { background: ACCENT_GOLD, color: '#1a1410' }
              : { background: `${GOLD} 0.06)`, color: TEXT_MUTED, border: `1px solid ${GOLD} 0.1)` }}
          >
            {w.label}
          </button>
        ))}
      </div>
    );
  }

  renderActivity() {
    const { activity, activityWindow } = this.state;
    if (!activity) return null;

    const { active = {}, windows } = activity;
    const selected = windows.find((w) => w.key === activityWindow) || windows[0];
    const modes = [
      { key: 'ranked', label: 'Ranked' },
      { key: 'casual', label: 'Casual' },
      { key: 'limited', label: 'Limited' },
      { key: 'sandbox', label: 'Sandbox' },
      { key: 'solo', label: 'Solo' },
    ];

    return (
      <>
        {/* DAU / WAU / MAU. Distinct players who opened the app, whether
            or not they played. A server from before login tracking still
            fills `active` from match history, so these cells keep the old
            engagement meaning there rather than breaking. */}
        <div className="grid grid-cols-4 gap-2 mt-2">
          {this.statCell('DAU', active.dau, 'logged in, 24h')}
          {this.statCell('WAU', active.wau, 'logged in, 7d')}
          {this.statCell('MAU', active.mau, 'logged in, 30d')}
          {this.statCell('Stickiness', `${active.stickiness}%`, 'DAU / MAU')}
        </div>

        {/* Per-window match breakdown with a window selector. */}
        {this.windowTabs(windows, activityWindow, (key) => this.setState({ activityWindow: key }))}

        <div className="grid grid-cols-3 gap-2 mt-2">
          {this.statCell('Matches', selected.matches?.toLocaleString())}
          {this.statCell('Players', selected.players?.toLocaleString(), 'played a match')}
          {this.statCell('Participations', selected.participations?.toLocaleString())}
        </div>

        {/* Mode split for the selected window. */}
        <div className="grid grid-cols-5 gap-2 mt-2">
          {modes.map((m) => {
            const d = selected.modeDetail?.[m.key];
            return this.statCell(
              m.label,
              (d?.matches ?? 0).toLocaleString(),
              `${(d?.players ?? 0).toLocaleString()} players`,
            );
          })}
        </div>

        {/* Browser vs desktop split. Absent on servers from before
            platform tracking, so the block hides itself gracefully. */}
        {activity.platform && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            {this.statCell('Online, browser', activity.platform.online?.web ?? 0, 'connected now')}
            {this.statCell('Online, desktop', activity.platform.online?.desktop ?? 0, 'connected now')}
            {this.statCell('Browser, 7d', activity.platform.connects?.web?.d7 ?? 0,
              `${activity.platform.connects?.web?.d30 ?? 0} in 30d`)}
            {this.statCell('Desktop, 7d', activity.platform.connects?.desktop?.d7 ?? 0,
              `${activity.platform.connects?.desktop?.d30 ?? 0} in 30d`)}
          </div>
        )}
      </>
    );
  }

  renderSignups() {
    const { activity, signupWindow } = this.state;
    // Absent on servers from before signup tracking shipped.
    const signups = activity?.signups;
    if (!signups) return null;

    const selected = signups.windows.find((w) => w.key === signupWindow) || signups.windows[0];
    const abandoned = (selected.total ?? 0) - (selected.onboarded ?? 0);

    return (
      <>
        {this.windowTabs(signups.windows, signupWindow, (key) => this.setState({ signupWindow: key }))}

        {/* An account exists from the moment auth succeeds; the username
            prompt comes after. The gap is the onboarding drop-off. */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          {this.statCell('New accounts', selected.total?.toLocaleString(),
            `${signups.total?.toLocaleString()} all time`)}
          {this.statCell('Finished onboarding', selected.onboarded?.toLocaleString(),
            `${signups.onboarded?.toLocaleString()} all time`)}
          {this.statCell('Never named', abandoned.toLocaleString(), 'no username chosen')}
        </div>
      </>
    );
  }

  render() {
    const { snapshotLoading, snapshot, activityLoading, bulkType, bulkAmount, showBulkConfirm, bulkGranting, targetName, targetType, targetAmount, targetGranting, goldAmount, packSet, packCount } = this.state;
    const { profile } = this.props;

    return (
      <>
        {/* ─── Economy Snapshot ────────────────────────── */}
        {hasCap(profile, 'economy.snapshot') && (
        <Section title="Economy Snapshot">
          <div className="flex items-center gap-2">
            <AdminButton onClick={this.fetchSnapshot} disabled={snapshotLoading}>
              {snapshotLoading ? 'Loading...' : 'Refresh'}
            </AdminButton>
          </div>
          {this.renderSnapshotGrid()}
        </Section>
        )}

        {/* ─── Player Activity ─────────────────────────── */}
        {hasCap(profile, 'economy.snapshot') && (
        <Section title="Player Activity">
          <div className="flex items-center gap-2">
            <AdminButton onClick={this.fetchActivity} disabled={activityLoading}>
              {activityLoading ? 'Loading...' : 'Refresh'}
            </AdminButton>
            <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
              Active = distinct players who logged in during the window.
            </span>
          </div>
          {this.renderActivity()}
        </Section>
        )}

        {/* ─── Signups ─────────────────────────────────── */}
        {hasCap(profile, 'economy.snapshot') && (
        <Section title="Signups">
          <div className="flex items-center gap-2">
            <AdminButton onClick={this.fetchActivity} disabled={activityLoading}>
              {activityLoading ? 'Loading...' : 'Refresh'}
            </AdminButton>
            <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
              New accounts created in the window, by account creation time.
            </span>
          </div>
          {this.renderSignups()}
        </Section>
        )}

        {/* ─── Bulk Grant ─────────────────────────────── */}
        {hasCap(profile, 'grant.bulkAll') && (
        <Section title="Bulk Grant">
          <div className="flex items-center gap-2">
            <select
              value={bulkType}
              onChange={(e) => this.setState({ bulkType: e.target.value })}
              className="px-2 py-1 text-xs"
              style={{ ...INPUT_STYLE, color: TEXT_BODY }}
            >
              <option value="coins">Gold</option>
              <option value="shards">Shards</option>
            </select>
            <input
              type="number"
              min="1"
              value={bulkAmount}
              onInput={(e) => this.setState({ bulkAmount: e.target.value })}
              className="w-24 px-2 py-1 text-xs text-center"
              style={INPUT_STYLE}
            />
            {!showBulkConfirm ? (
              <AdminButton onClick={() => this.setState({ showBulkConfirm: true })} disabled={bulkGranting}>
                Grant to All Players
              </AdminButton>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-[0.6875cqw]" style={{ color: TEXT_BODY }}>
                  Grant {bulkAmount} {bulkType} to {snapshot?.active_players ?? '?'} active players?
                </span>
                <button
                  type="button"
                  className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.03]"
                  style={GOLD_BTN}
                  onClick={this.handleBulkGrant}
                  disabled={bulkGranting}
                >
                  {bulkGranting ? 'Granting...' : 'Confirm'}
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.03]"
                  style={DANGER_BTN}
                  onClick={() => this.setState({ showBulkConfirm: false })}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </Section>
        )}

        {/* ─── Grant to Specific Player ───────────────── */}
        {hasCap(profile, 'grant.currency') && (
        <Section title="Grant to Player">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Player name"
              value={targetName}
              onInput={(e) => this.setState({ targetName: e.target.value })}
              className="px-2 py-1 text-xs"
              style={{ ...INPUT_STYLE, width: '8.75cqw' }}
            />
            <select
              value={targetType}
              onChange={(e) => this.setState({ targetType: e.target.value })}
              className="px-2 py-1 text-xs"
              style={{ ...INPUT_STYLE, color: TEXT_BODY }}
            >
              <option value="coins">Gold</option>
              <option value="shards">Shards</option>
            </select>
            <input
              type="number"
              value={targetAmount}
              onInput={(e) => this.setState({ targetAmount: e.target.value })}
              className="w-24 px-2 py-1 text-xs text-center"
              style={INPUT_STYLE}
            />
            <AdminButton onClick={this.handleTargetGrant} disabled={targetGranting || !targetName.trim()}>
              {targetGranting ? 'Granting...' : 'Grant'}
            </AdminButton>
          </div>
          <div className="text-[0.625cqw] mt-1" style={{ color: TEXT_MUTED }}>
            Negative amounts subtract. Clamped to 0.
          </div>
        </Section>
        )}

        {/* ─── Per-Player: Collection ─────────────────── */}
        {hasCap(profile, 'grant.cards') && (
        <Section title="Collection">
          <div className="flex flex-wrap gap-2">
            <AdminButton onClick={this.handleSeedFoils}>Seed Foil Cards</AdminButton>
            <AdminButton onClick={this.handleGrantAllCards}>Grant All Cards ×4</AdminButton>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>Open packs:</span>
            <input
              type="number"
              min="1"
              max="100"
              value={packCount}
              onInput={(e) => this.setState({ packCount: e.target.value })}
              className="w-16 px-2 py-1 text-xs text-center"
              style={INPUT_STYLE}
            />
            <select
              value={packSet}
              onChange={(e) => this.setState({ packSet: e.target.value })}
              className="px-2 py-1 text-xs"
              style={{ ...INPUT_STYLE, color: TEXT_BODY }}
            >
              <option value="gothic">Gothic</option>
              <option value="arthurian">Arthurian</option>
              <option value="beta">Beta</option>
            </select>
            <AdminButton onClick={this.handleGeneratePacks}>Open & Add</AdminButton>
          </div>
        </Section>
        )}

        {/* ─── Per-Player: Economy ────────────────────── */}
        {hasCap(profile, 'grant.currency') && (
        <Section title="Add Gold">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              value={goldAmount}
              onInput={(e) => this.setState({ goldAmount: e.target.value })}
              className="w-24 px-2 py-1 text-xs text-center"
              style={INPUT_STYLE}
            />
            <AdminButton onClick={this.handleAddGold}>Add Gold</AdminButton>
          </div>
        </Section>
        )}

        {/* ─── Per-Player: Progression Test ────────────── */}
        {hasCap(profile, 'progression.setLevel') && (
        <Section title="Progression (Test)">
          <div className="text-[0.625cqw] mb-2" style={{ color: TEXT_MUTED }}>
            Raises or lowers your level so you can test the Progression screen
            without grinding matches. Lowering clamps claim cursors so cells
            past the new level don't show as "claimed".
          </div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>Set level:</span>
            <input
              type="number"
              min="1"
              max="30"
              value={this.state.progressionLevel}
              onInput={(e) => this.setState({ progressionLevel: e.target.value })}
              className="w-16 px-2 py-1 text-xs text-center"
              style={INPUT_STYLE}
            />
            <AdminButton
              onClick={() => {
                const lvl = parseInt(this.state.progressionLevel, 10);
                if (Number.isInteger(lvl) && lvl >= 1 && lvl <= 30) this.handleSetLevel(lvl);
              }}
              disabled={this.state.progressionBusy}
            >
              Apply
            </AdminButton>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {[1, 2, 3, 5, 10, 15, 20, 25, 30].map((lvl) => (
              <AdminButton
                key={lvl}
                onClick={() => this.handleSetLevel(lvl)}
                disabled={this.state.progressionBusy}
              >
                Lvl {lvl}
              </AdminButton>
            ))}
          </div>
          {hasCap(profile, 'progression.resetClaims') && (
            <div className="flex items-center gap-2">
              <AdminButton
                onClick={this.handleResetProgressionClaims}
                disabled={this.state.progressionBusy}
              >
                Reset Gold + SP Claims
              </AdminButton>
              <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                Re-opens every gold/SP cell up to your current level. Decks untouched.
              </span>
            </div>
          )}
        </Section>
        )}
      </>
    );
  }
}
